import { PRM_CombinedResponse } from '../types/unified_models'

export const stripJsonSuffix = (name?: string | null): string | undefined => {
  if (typeof name !== 'string') return name === null ? undefined : name
  return name.replace(/\.json$/i, '')
}

const normalizeBenchmarkResult = (result: any): any => {
  if (!result) return result
  const normalized: any = { ...result }
  normalized.benchmark_name = stripJsonSuffix(result.benchmark_name)
  return normalized
}

const normalizeBenchmark = (benchmark: any): any => {
  if (!benchmark || (typeof benchmark === 'object' && 'error' in benchmark)) {
    return benchmark
  }

  const normalized: any = { ...benchmark }

  // Normalize reference
  if (Array.isArray(benchmark.reference)) {
    normalized.reference = (benchmark.reference as any[]).map(normalizeBenchmarkResult)
  } else if (benchmark.reference) {
    normalized.reference = normalizeBenchmarkResult(benchmark.reference)
  }

  // Normalize submission
  if (Array.isArray(benchmark.submission)) {
    normalized.submission = (benchmark.submission as any[]).map(normalizeBenchmarkResult)
  } else if (benchmark.submission) {
    normalized.submission = normalizeBenchmarkResult(benchmark.submission)
  }

  // Normalize challenges
  if (Array.isArray(benchmark.challenges)) {
    normalized.challenges = (benchmark.challenges as any[]).map(normalizeBenchmarkResult)
  }

  // Normalize test case names if present
  if (benchmark.tests && Array.isArray(benchmark.tests.test_cases)) {
    normalized.tests = {
      ...benchmark.tests,
      test_cases: benchmark.tests.test_cases.map((tc: any) => ({
        ...tc,
        name: stripJsonSuffix(tc.name),
      })),
    }
  }

  // If submission ran on a single input but reference/challenges include many,
  // filter them down to the matching input to avoid mixing cached results
  const subArray: any[] = Array.isArray(normalized.submission)
    ? normalized.submission
    : normalized.submission
    ? [normalized.submission]
    : []
  const refArray: any[] = Array.isArray(normalized.reference)
    ? normalized.reference
    : normalized.reference
    ? [normalized.reference]
    : []

  if (subArray.length === 1 && refArray.length > 1) {
    const target = subArray[0]?.benchmark_name
    if (target) {
      const targetNorm = String(stripJsonSuffix(target)).toLowerCase()
      const matchesStrict = (n?: string) => String(stripJsonSuffix(n || '')).toLowerCase() === targetNorm
      normalized.reference = refArray.filter((r: any) => matchesStrict(r.benchmark_name))
      if (Array.isArray(normalized.challenges)) {
        normalized.challenges = (normalized.challenges as any[]).filter((c: any) => matchesStrict(c.benchmark_name))
      }
    }
  }

  return normalized
}

const normalizeCorrectness = (correctness: any): any => {
  if (!correctness || (typeof correctness === 'object' && 'error' in correctness)) {
    return correctness
  }
  if (!Array.isArray(correctness.test_cases)) return correctness
  return {
    ...correctness,
    test_cases: correctness.test_cases.map((tc: any) => ({
      ...tc,
      name: stripJsonSuffix(tc.name),
    })),
  }
}

export const normalizeCombinedResponseInputs = (
  response: PRM_CombinedResponse
): PRM_CombinedResponse => {
  const normalized: any = { ...response }

  if (response.benchmark) {
    normalized.benchmark = normalizeBenchmark(response.benchmark)
  }

  if (response.correctness) {
    normalized.correctness = normalizeCorrectness(response.correctness)
  }

  if (response.custom) {
    normalized.custom = normalizeCorrectness(response.custom)
  }

  if (response.tiers && !(typeof response.tiers === 'object' && 'error' in response.tiers)) {
    const t: any = response.tiers
    if (t && t.tests) {
      normalized.tiers = {
        ...t,
        tests: normalizeCorrectness(t.tests),
      }
    }
  }

  // No normalization needed for profiler, cilksan, or cilkscale_benchmark
  return normalized as PRM_CombinedResponse
}


